import React, { useState, useEffect } from 'react';
import './BannerSelectionModal.css';
import { bannerAPI } from '../../../config/banner-api';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';

const BannerSelectionModal = ({ isOpen, onClose, type, targetData }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedBanners, setSelectedBanners] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchBanners();
    }
  }, [isOpen]);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await bannerAPI.getAllBanners();
      if (response && response.success) {
        const allBanners = response.data?.banners || response.data || [];
        processBanners(allBanners);
      } else {
        toast.error(response?.msg || "Failed to load banners");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error loading banners");
    } finally {
      setLoading(false);
    }
  };

  const processBanners = (allBanners) => {
    let relevantBanners = [];
    let initialSelected = new Set();

    if (type === 'product') {
      // list all banners with products > 0
      relevantBanners = allBanners.filter(b => b.products && b.products.length > 0);

      relevantBanners.forEach(b => {
        if (b.products.some(p => String(p.id) === String(targetData.productId) || String(p.productId) === String(targetData.productId) || String(p) === String(targetData.productId))) {
          initialSelected.add(b._id);
        }
      });
    } else if (type === 'subcategory') {
      // list banners with products == 0 and category matches
      relevantBanners = allBanners.filter(b =>
        (!b.products || b.products.length === 0) &&
        b.category && b.category.toLowerCase() === targetData.category.toLowerCase()
      );

      relevantBanners.forEach(b => {
        if (b.subCategory && b.subCategory.toLowerCase() === targetData.subCategory.toLowerCase()) {
          initialSelected.add(b._id);
        }
      });
    }

    relevantBanners.sort((a, b) => {
      const aSelected = initialSelected.has(a._id) ? 1 : 0;
      const bSelected = initialSelected.has(b._id) ? 1 : 0;
      return bSelected - aSelected;
    });

    setBanners(relevantBanners);
    setSelectedBanners(initialSelected);
  };

  const handleToggleSelection = (bannerId) => {
    setSelectedBanners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bannerId)) {
        newSet.delete(bannerId);
      } else {
        newSet.add(bannerId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload = {};

      if (type === 'product') {
        payload = {
          productId: targetData.productId,
          bannerIds: Array.from(selectedBanners)
        };
      } else if (type === 'subcategory') {
        payload = {
          category: targetData.category,
          subCategory: targetData.subCategory,
          bannerIds: Array.from(selectedBanners)
        };
      }

      const response = await bannerAPI.bulkUpdateBannerProducts(payload);

      if (response && response.success) {
        toast.success(response.msg || "Banners updated successfully");
        onClose();
      } else {
        toast.error(response?.msg || "Failed to update banners");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating banners");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bannerSelection-overlay">
      <div className="bannerSelection-modal">
        <div className="bannerSelection-header">
          <h2>{type === 'product' ? 'Banners & Sections with this Product' : type === 'subcategory' ? 'Banners & Sections with this Subcategory' : 'Banners & Sections with this Category'}</h2>
          <button onClick={onClose} className="bannerSelection-close" type="button">
            <X size={20} />
          </button>
        </div>
        <div className="bannerSelection-body">
          {loading ? (
            <div className="bannerSelection-loading">Loading banners...</div>
          ) : (
            <div className="bannerSelection-list-container">
              {banners.length === 0 ? (
                <div className="bannerSelection-no-results">No relevant banners found.</div>
              ) : (
                banners.map(banner => (
                  <div key={banner._id} className="bannerSelection-item">
                    <label className="bannerSelection-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedBanners.has(banner._id)}
                        onChange={() => handleToggleSelection(banner._id)}
                      />
                      <div className="bannerSelection-item-info">
                        {banner.image ? (
                          <img src={banner.image} alt="" className="bannerSelection-item-img" />
                        ) : (
                          <div className="bannerSelection-item-no-image">No IMG</div>
                        )}
                        <div className="bannerSelection-item-details">
                          <span className="bannerSelection-item-title">{banner.title}</span>
                          {banner.type && (
                            <span className="bannerSelection-item-type" style={{ fontSize: '12px', color: '#666', marginTop: '2px', display: 'block' }}>
                              {banner.type === 'banner_slider' ? 'Top Banner Slider' : banner.type === 'top_product' ? 'Products only section' : 'Banner + products section'}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className="bannerSelection-footer">
          <button onClick={onClose} disabled={saving} className="bannerSelection-btn-secondary" type="button">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="bannerSelection-btn-primary" type="button">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BannerSelectionModal;
